import React from 'react';

// 카테고리 순서지정 용
export const CATEGORY_ORDER = ['항공법규', '정비일반', '항공기체', '항공발동기', '전자·전기·계기(기본)'];
export const CATEGORY_ICON = {
  항공법규: '📖',
  정비일반: '⚙️',
  항공기체: '✈️',
  항공발동기: '🚀',
  '전자·전기·계기(기본)': '⚡',
  최종시험: '💯',
};

// 지정과목 "최종시험"은 배정 화면에서 한눈에 구분되도록 이름 옆에 💯를 붙여 보여준다.
export function subjectDisplayLabel(name) {
  return name === '최종시험' ? `${name} 💯` : name;
}

export function partNumber(label) {
  const m = /PART\s*(\d+)/i.exec(label || '');
  return m ? parseInt(m[1], 10) : Infinity;
}

export function buildTree(rows) {
  const root = { id: 'root', type: 'root', label: '교육과정', children: [] };

  // 카테고리 목록/순서는 서버가 amtcode 순으로 이미 정렬해서 내려주므로,
  // 하드코딩된 목록 대신 응답에 실제로 등장한 순서를 그대로 사용한다
  // (새 과목이 추가돼도 화면에서 누락되지 않도록).
  const categoryNames = [];
  rows.forEach((r) => {
    if (!categoryNames.includes(r.cat)) categoryNames.push(r.cat);
  });

  categoryNames.forEach((catName) => {
    const catRows = rows.filter((r) => r.cat === catName);
    if (catRows.length === 0) return;

    const tbSubject = catRows[0].tbsubj;
    const catNode = {
      id: `cat:${catName}`,
      type: 'category',
      label: catName,
      icon: CATEGORY_ICON[catName] || '📘',
      tbSubject,
      allParts: catRows
        .slice()
        .sort((a, b) => partNumber(a.tbpart) - partNumber(b.tbpart))
        .map((r) => ({ crsmatno: r.crsmatno, label: r.tbpart, theory: r.theory, practice: r.practice })),
      children: [],
    };

    const chapterMap = new Map();
    catRows.forEach((r) => {
      if (!chapterMap.has(r.chapter)) {
        chapterMap.set(r.chapter, { theory: r.theory, practice: r.practice, parts: [] });
      }
      chapterMap.get(r.chapter).parts.push({ crsmatno: r.crsmatno, label: r.tbpart, theory: r.theory, practice: r.practice });
    });

    chapterMap.forEach((val, chapterLabel) => {
      catNode.children.push({
        id: `ch:${catName}:${chapterLabel}`,
        type: 'chapter',
        label: chapterLabel,
        theory: val.theory,
        practice: val.practice,
        tbSubject,
        relatedParts: val.parts.slice().sort((a, b) => partNumber(a.label) - partNumber(b.label)),
      });
    });

    root.children.push(catNode);
  });

  return root;
}

export function buildIndex(node, parent, index) {
  index[node.id] = { node, parent };
  (node.children || []).forEach((c) => buildIndex(c, node, index));
}

export function iconFor(node) {
  if (node.icon) return node.icon;
  return '📘';
}

export function collectCheckedDragItems(parts, sectionsCache, checkedRows) {
  const items = [];
  parts.forEach((p) => {
    if (checkedRows.has(`part:${p.crsmatno}`)) {
      items.push({ crsmatno: p.crsmatno, label: p.label });
    }
    const sections = sectionsCache[p.crsmatno];
    if (sections) {
      sections.forEach((s) => {
        if (checkedRows.has(`sect:${s.crssectno}`)) {
          items.push({ crsmatno: p.crsmatno, label: p.label, crssectno: s.crssectno, sectionLabel: s.sections });
        }
      });
    }
  });
  return items;
}

export function dragPayload(e, items) {
  e.dataTransfer.setData('application/json', JSON.stringify(items));
}

export function TreeNodeItem({ node, expanded, selectedId, onToggle }) {
  const isOpen = expanded.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = node.id === selectedId;
  const displayIcon = node.type === 'chapter' && isSelected ? '📖' : iconFor(node);
  return (
    <li>
      <div
        className={`curr-node-row${isSelected ? ' selected' : ''}`}
        onClick={() => onToggle(node.id)}
      >
        <span className={`curr-twisty${hasChildren ? (isOpen ? ' open' : '') : ' hidden'}`}>▸</span>
        <span className="curr-node-icon">{displayIcon}</span>
        <span className="curr-node-label">{node.label}</span>
        {(node.type === 'chapter' || node.type === 'part') && (
          <span className="curr-node-hours">{node.theory}/{node.practice}</span>
        )}
      </div>
      {hasChildren && isOpen && (
        <ul className="curr-tree-list">
          {node.children.map((c) => (
            <TreeNodeItem key={c.id} node={c} expanded={expanded} selectedId={selectedId} onToggle={onToggle} />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * 표준교재 패널 - 과목 -> PART -> 섹션, 표준교재 자체의 구조를 그대로 따르는 탐색기
 * (지정교과목의 "챕터"는 법정 기준 분류일 뿐 표준교재 고유의 단계가 아니므로 여기서는 쓰지 않는다).
 * 좌측 지정교과목 트리와 별개로 자체 펼침 상태(tbExpanded)로 단독 동작하며,
 * 지정교과목 트리에서 과목을 펼치거나 챕터를 선택하면 그 상태가 여기에도 반영된다.
 * 체크박스/드래그 배정은 지정교과목에서 선택된 챕터에 속한 PART(activeCrsmatnos)에만 활성화된다.
 */
export function TextbookExplorer({
  categories,
  tbExpanded,
  onToggleTb,
  expandedParts,
  onTogglePart,
  sectionsCache,
  checkedRows,
  onToggleCheckRow,
  activeCrsmatnos,
}) {
  if (!categories || categories.length === 0) {
    return <div className="curr-empty-state"><span className="curr-empty-big">📂</span>표준교재 정보가 없습니다.</div>;
  }

  return (
    <ul className="curr-tb-category-list">
      {categories.map((cat) => {
        const catOpen = tbExpanded.has(cat.id);
        return (
          <li key={cat.id}>
            <div className="curr-node-row curr-tb-category-row" onClick={() => onToggleTb(cat.id)}>
              <span className={`curr-twisty${catOpen ? ' open' : ''}`}>▸</span>
              <span className="curr-node-icon">📘</span>
              <span className="curr-node-label">{cat.tbSubject}</span>
              <span className="curr-chip curr-chip-count"><span className="curr-num">{cat.allParts.length}</span> 개 PART</span>
            </div>
            {catOpen && (
              <div className="curr-tb-chapter-parts">
                <TextbookHierarchy
                  keyId={cat.id}
                  parts={cat.allParts}
                  expandedParts={expandedParts}
                  onTogglePart={onTogglePart}
                  sectionsCache={sectionsCache}
                  activeCrsmatnos={activeCrsmatnos}
                  checkedRows={checkedRows}
                  onToggleCheckRow={onToggleCheckRow}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function TextbookHierarchy({
  keyId,
  parts,
  expandedParts,
  onTogglePart,
  sectionsCache,
  activeCrsmatnos = null,
  checkedRows,
  onToggleCheckRow,
}) {
  if (!parts || parts.length === 0) {
    return <div className="curr-empty-state"><span className="curr-empty-big">📂</span>매칭되는 표준교재 정보가 없습니다.</div>;
  }

  return (
      <ul className="curr-tb-part-list">
        {parts.map((p) => {
          const key = `${keyId}::${p.crsmatno}`;
          const open = expandedParts.has(key);
          const sections = sectionsCache[p.crsmatno];
          const partRowKey = `part:${p.crsmatno}`;
          // 지정교과목에서 챕터를 선택했든 안 했든, 표준교재의 모든 PART는 체크·드래그로 배정 가능하다.
          // isActiveChapter는 순전히 "지금 선택된 챕터에 속한 PART"라는 참고 표시용이다.
          const isActiveChapter = !!activeCrsmatnos && activeCrsmatnos.has(p.crsmatno);
          const partChecked = checkedRows.has(partRowKey);
          return (
            <li key={p.crsmatno}>
              <div
                className={`curr-node-row curr-tb-part-row${isActiveChapter ? ' active' : ''}`}
                draggable={partChecked}
                onDragStart={(e) => dragPayload(e, collectCheckedDragItems(parts, sectionsCache, checkedRows))}
              >
                <span className={`curr-twisty${open ? ' open' : ''}`} onClick={() => onTogglePart(keyId, p.crsmatno)}>▸</span>
                <span className="curr-node-icon">📑</span>
                <span className="curr-node-label" onClick={() => onTogglePart(keyId, p.crsmatno)}>{p.label}</span>
                <span className="curr-node-hours">{p.theory}/{p.practice}</span>
                <input
                  type="checkbox"
                  className="curr-row-check"
                  checked={partChecked}
                  onChange={() => onToggleCheckRow(partRowKey)}
                />
              </div>
              {open && (
                <div className="curr-section-list">
                  {!sections && <div className="curr-section-loading">불러오는 중...</div>}
                  {sections && sections.length === 0 && (
                    <div className="curr-section-placeholder">등록된 세부 섹션이 없습니다.</div>
                  )}
                  {sections && sections.map((s) => {
                    const sectRowKey = `sect:${s.crssectno}`;
                    const sectChecked = checkedRows.has(sectRowKey);
                    return (
                      <div key={s.crssectno} className="curr-section-item">
                        <input
                          type="checkbox"
                          className="curr-row-check"
                          checked={sectChecked}
                          onChange={() => onToggleCheckRow(sectRowKey)}
                        />
                        <span
                          className="curr-section-item-label"
                          draggable={sectChecked}
                          onDragStart={(e) => dragPayload(e, collectCheckedDragItems(parts, sectionsCache, checkedRows))}
                        >
                          {s.sections}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ul>
  );
}
